import { useState } from 'react';
import {
  CHANGE_INSURANCE_LABEL,
  fleetInsuranceSummary,
  sumAssetInsuranceCents,
} from './fleetInsurance';
import { Asset } from '../../../../../../../types/apiTypes';
import FleetInsuranceModal from './FleetInsuranceModal';
import { useFleetInsurance } from './useFleetInsurance';

interface FleetInsuranceControlProps {
  /** The customer's assets — used to show what per-truck amounts sum to. */
  assets: Asset[];
  /**
   * True while the assets query is still in flight. The Each Truck total is
   * summed from `assets`, so an empty-because-loading array would otherwise be
   * reported as a real "$0.00/mo total" (W3).
   */
  loadingAssets?: boolean;
  /** Demo sessions render the control read-only rather than PATCHing. */
  disabled?: boolean;
}

/**
 * THT-675 — "one insurance number for my whole fleet" for the assets tab.
 *
 * Sits in the customer-assets widget header so the choice is made in the same
 * place the assets it governs are listed. The mode is set once at onboarding
 * and rarely touched, so the steady state here is a single summary line and the
 * editing UI lives in `FleetInsuranceModal` — the asset table, not this
 * control, owns the top of the tab (collapsed-UI spec §2).
 *
 * The summary reads straight off `useFleetInsurance()`, which stays the single
 * source of truth: an admin flipping the mode elsewhere re-renders this line
 * with no reload and no local copy of the record to fall out of sync. In Each
 * Truck mode the figure is summed from the `assets` this component is already
 * handed (spec §7/R2.4), so it re-derives on every render an asset edit causes.
 */
export default function FleetInsuranceControl({
  assets,
  loadingAssets = false,
  disabled = false,
}: FleetInsuranceControlProps) {
  const {
    mode: savedMode,
    fleetAmountCents: savedAmountCents,
    canEditFleetInsurance,
  } = useFleetInsurance();

  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!canEditFleetInsurance) return null;

  return (
    <div className="flex w-full flex-row flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b-[1px] border-bordergray px-4 py-3">
      <p className="min-w-0 break-words text-[14px] text-primary">
        {fleetInsuranceSummary(
          savedMode,
          savedAmountCents,
          sumAssetInsuranceCents(assets),
          loadingAssets
        )}
      </p>
      {/* Demo sessions still see the setting — they just cannot open the editor. */}
      <button
        type="button"
        aria-haspopup="dialog"
        disabled={disabled}
        onClick={() => setIsModalOpen(true)}
        className="shrink-0 rounded-full border-[1px] border-bordergray px-4 py-1 text-[13px] font-medium text-primary transition-colors disabled:cursor-not-allowed disabled:opacity-60"
      >
        {CHANGE_INSURANCE_LABEL}
      </button>
      <FleetInsuranceModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        assets={assets}
      />
    </div>
  );
}
